import React from "react";
import { motion } from "framer-motion";
import { useReveal } from "../../hooks/useReveal";
import { EASE, DURATIONS, prefersReducedMotion } from "../../lib/motion";

/**
 * SectionAnchor — 90vh full-bleed editorial breathing beat between typographic
 * sections. Slow scale-in reveal, optional bottom-left "NEXT — …" eyebrow.
 * Marked `data-section-tone="dark"` so the top nav dissolves to transparent
 * over it (see Nav IntersectionObserver logic).
 * Respects prefers-reduced-motion.
 */
export const SectionAnchor = ({
  image,
  alt = "",
  eyebrow,
  objectPosition = "center",
  testId,
}) => {
  const reduce = prefersReducedMotion();
  const [ref, visible] = useReveal({ threshold: 0.15 });

  return (
    <section
      ref={ref}
      data-testid={testId || "section-anchor"}
      data-section-tone="dark"
      className="relative w-full h-[90vh] min-h-[520px] overflow-hidden bg-navy"
    >
      <motion.img
        src={image}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition }}
        initial={reduce ? false : { scale: 1.05 }}
        animate={reduce ? { scale: 1 } : { scale: visible ? 1.0 : 1.05 }}
        transition={{
          duration: reduce ? 0 : DURATIONS.anchorScaleIn,
          ease: EASE,
        }}
      />
      {/* Subtle navy vignette — top-heavy for nav legibility, bottom for eyebrow legibility */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,19,42,0.45) 0%, rgba(15,19,42,0.10) 20%, rgba(15,19,42,0.00) 55%, rgba(15,19,42,0.35) 85%, rgba(15,19,42,0.55) 100%)",
        }}
      />

      {eyebrow ? (
        <div
          className="absolute bottom-0 left-0 flex items-center gap-4 pointer-events-none"
          style={{ padding: "0 0 48px 48px" }}
        >
          <span aria-hidden="true" className="block h-px bg-gold" style={{ width: 24 }} />
          <span
            className="font-sans uppercase text-offwhite/55"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            {eyebrow}
          </span>
        </div>
      ) : null}
    </section>
  );
};

export default SectionAnchor;
